import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import AdminLayout from '@/components/layout/AdminLayout';
import SuperAdminLayout from '@/components/layout/SuperAdminLayout';
import CandidateAccountDialogs from '@/components/CandidateAccountDialogs';
import {
  GuestOnly,
  RequireAdmin,
  RequireCandidate,
  RequireSuperAdmin,
  SAIndexRoute,
  RequirePermission,
  RootRedirect,
} from '@/routes/guards';
import LoginRoute from '@/routes/LoginRoute';
import {
  InstructionsRoute,
  InterviewRoute,
  InterviewCompletedRoute,
} from '@/routes/InterviewFlowRoutes';

import DesignPreviewPage from '@/pages/DesignPreviewPage';
import CandidateDashboard from '@/pages/CandidateDashboard';
import DetailedReportPage from '@/pages/DetailedReportPage';
import AdminPanel from '@/components/AdminPanel';
import NotFoundPage from '@/pages/NotFoundPage';
import ServerErrorPage from '@/pages/ServerErrorPage';
import ErrorBoundary from '@/components/ux/ErrorBoundary';
import NetworkErrorPage from '@/pages/NetworkErrorPage';
import ScheduleInterviewPage from '@/pages/ScheduleInterviewPage';
import ScheduleInterviewNewPage from '@/pages/ScheduleInterviewNewPage';
import CreateUserPage from '@/pages/CreateUserPage';
import TemplatesPage from '@/pages/TemplatesPage';
import TemplateFormPage from '@/pages/TemplateFormPage';
import ReportsPage from '@/pages/ReportsPage';
import SettingsPage from '@/pages/SettingsPage';
import SuperAdminDashboard from '@/pages/SuperAdminDashboard';
import SuperAdminCompaniesPage from '@/pages/SuperAdminCompaniesPage';
import SuperAdminCompanyDetailPage from '@/pages/SuperAdminCompanyDetailPage';
import SuperAdminPlansPage from '@/pages/SuperAdminPlansPage';
import SuperAdminBillingPage from '@/pages/SuperAdminBillingPage';
import SuperAdminBillingSettingsPage from '@/pages/SuperAdminBillingSettingsPage';
import VerifyBillingEmailPage from '@/pages/VerifyBillingEmailPage';
import SuperAdminEarningsPage from '@/pages/SuperAdminEarningsPage';
import SuperAdminTrialsPage from '@/pages/SuperAdminTrialsPage';
import SuperAdminNotificationsPage from '@/pages/SuperAdminNotificationsPage';
import AdminMyPlanPage from '@/pages/AdminMyPlanPage';
import ResumePage from '@/pages/ResumePage';
import InterviewMasterPage from '@/pages/InterviewMasterPage';
import CandidateProcessPage from '@/pages/CandidateProcessPage';
import ConsolidatedReportPage from '@/pages/ConsolidatedReportPage';
import AdminSupportPage from '@/pages/AdminSupportPage';
import SuperAdminSupportPage from '@/pages/SuperAdminSupportPage';
import QuestionSetsPage from '@/pages/QuestionSetsPage';
import SuperAdminLeadsPage from '@/pages/SuperAdminLeadsPage';
import SuperAdminProspectsPage       from '@/pages/SuperAdminProspectsPage';
import SuperAdminEmailTemplatesPage  from '@/pages/SuperAdminEmailTemplatesPage';
import SuperAdminHealthPage          from '@/pages/SuperAdminHealthPage';
import SuperAdminTeamPage           from '@/pages/SuperAdminTeamPage';
import PublicJobPage                from '@/pages/PublicJobPage';

/**
 * Decides when the whole application is unusable — and only then.
 *
 * It used to treat every unhandled promise rejection as a fatal error and swap
 * the entire route tree for a 500 page. A single failed API call did that, and
 * because the flag was never cleared, Back and Home changed the URL while the
 * same error page stayed on screen. Three separate complaints, one cause.
 *
 * Now there are exactly two app-wide conditions:
 *   - the browser is offline, which clears itself the moment it reconnects
 *   - React could not render, caught by the boundary below
 *
 * A failed request is neither. It belongs to the screen that made it, which
 * can show a toast or an inline message and let the user carry on.
 */
function AppErrorShell({ children }) {
  const location = useLocation();
  const [offline, setOffline] = useState(() =>
    typeof navigator !== 'undefined' && navigator.onLine === false
  );

  useEffect(() => {
    const goOnline  = () => setOffline(false);
    const goOffline = () => setOffline(true);

    // Logged, not fatal. Losing one request must not cost the user the page
    // they are on, along with anything they had typed into it.
    const onRejection = (event) => {
      console.error('[unhandled rejection]', event.reason);
    };

    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  if (offline) {
    return <NetworkErrorPage onRetry={() => window.location.reload()} />;
  }

  return (
    <ErrorBoundary
      resetKey={location.pathname}
      fallback={({ reset }) => <ServerErrorPage onReset={reset} />}
    >
      {children}
    </ErrorBoundary>
  );
}

export default function AppRoutes() {
  return (
    <AppErrorShell>
      <Routes>
        <Route path="/" element={<RootRedirect />} />
        <Route path="/design-preview" element={<DesignPreviewPage />} />

        <Route path="/login" element={<GuestOnly><LoginRoute /></GuestOnly>} />

        {/* Public job posting — shared externally, no authentication */}
        <Route path="/jobs/:slug" element={<PublicJobPage />} />
        <Route path="/verify-billing-email" element={<VerifyBillingEmailPage />} />

        <Route path="/instructions/:uniqueLink" element={<InstructionsRoute />} />
        <Route path="/interview/:uniqueLink" element={<InterviewRoute />} />
        <Route path="/interview-completed" element={<InterviewCompletedRoute />} />
        <Route path="/reports/:sessionId" element={<DetailedReportPage />} />

        <Route
          path="/dashboard"
          element={
            <RequireCandidate>
              <>
                <CandidateDashboard />
                <CandidateAccountDialogs />
              </>
            </RequireCandidate>
          }
        />

        <Route
          path="/super-admin"
          element={
            <RequireSuperAdmin>
              <SuperAdminLayout />
            </RequireSuperAdmin>
          }
        >
          <Route index element={<SAIndexRoute />} />
          <Route path="companies" element={<SuperAdminCompaniesPage />} />
          <Route path="companies/:companyId" element={<SuperAdminCompanyDetailPage />} />
          <Route path="plans" element={<SuperAdminPlansPage />} />
          <Route path="billing" element={<SuperAdminBillingPage />} />
          <Route path="billing/settings" element={<SuperAdminBillingSettingsPage />} />
          <Route path="earnings" element={<SuperAdminEarningsPage />} />
          <Route path="trials" element={<SuperAdminTrialsPage />} />
          <Route path="notifications" element={<SuperAdminNotificationsPage />} />
          <Route path="support" element={<SuperAdminSupportPage />} />
          <Route path="leads" element={<SuperAdminLeadsPage />} />
          <Route path="prospects" element={<SuperAdminProspectsPage />} />
          <Route path="email-templates" element={<SuperAdminEmailTemplatesPage />} />
          <Route path="health"          element={<SuperAdminHealthPage />} />
          <Route path="team"            element={<SuperAdminTeamPage />} />
        </Route>

        <Route
          path="/admin"
          element={
            <RequireAdmin>
              <AdminLayout />
            </RequireAdmin>
          }
        >
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={
            <RequirePermission permission="view_dashboard">
              <AdminPanel />
            </RequirePermission>
          } />
          <Route path="schedule" element={
            <RequirePermission permission="schedule_interviews">
              <ScheduleInterviewPage />
            </RequirePermission>
          } />
          <Route path="schedule/new" element={
            <RequirePermission permission="schedule_interviews">
              <ScheduleInterviewNewPage />
            </RequirePermission>
          } />
          <Route path="create-user" element={
            <RequirePermission permission="view_team">
              <CreateUserPage />
            </RequirePermission>
          } />
          <Route path="templates" element={
            <RequirePermission permission="view_templates">
              <TemplatesPage />
            </RequirePermission>
          } />
          <Route path="templates/new" element={
            <RequirePermission permission="manage_templates">
              <TemplateFormPage />
            </RequirePermission>
          } />
          <Route path="templates/:id/edit" element={
            <RequirePermission permission="manage_templates">
              <TemplateFormPage />
            </RequirePermission>
          } />
          <Route path="resumes" element={
            <RequirePermission permission="view_resumes">
              <ResumePage />
            </RequirePermission>
          } />
          <Route path="reports" element={
            <RequirePermission permission="view_reports">
              <ReportsPage />
            </RequirePermission>
          } />
          <Route path="reports/:sessionId" element={
            <RequirePermission permission="view_reports">
              <DetailedReportPage />
            </RequirePermission>
          } />
          <Route path="my-plan" element={
            <RequirePermission permission="view_plan">
              <AdminMyPlanPage />
            </RequirePermission>
          } />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="masters" element={
            <RequirePermission permission="manage_masters">
              <InterviewMasterPage />
            </RequirePermission>
          } />
          <Route path="candidate-process" element={
            <RequirePermission permission="view_pipeline">
              <CandidateProcessPage />
            </RequirePermission>
          } />
          <Route path="candidate-process/:processId" element={
            <RequirePermission permission="view_pipeline">
              <ConsolidatedReportPage />
            </RequirePermission>
          } />
          <Route path="support" element={
            <RequirePermission permission="use_support">
              <AdminSupportPage />
            </RequirePermission>
          } />
          <Route path="question-sets" element={
            <RequirePermission permission="schedule_interviews">
              <QuestionSetsPage />
            </RequirePermission>
          } />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </AppErrorShell>
  );
}
